import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  TextField,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  InputAdornment,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import kycService from '../services/kycService';
import { useToast } from '../contexts/ToastContext';

const KYCVerification = () => {
  const theme = useTheme();
  const { showToast } = useToast();

  // State management
  const [kycSubmissions, setKycSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);

  // Pagination and filtering
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [incomeFilter, setIncomeFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');

  // Statistics
  const [statistics, setStatistics] = useState({
    pending: 0,
    verified: 0,
    rejected: 0,
    expired: 0,
    total: 0
  });

  // Verification dialog state
  const [verificationDialog, setVerificationDialog] = useState({
    open: false,
    type: '', // 'verify' or 'reject'
    submissionId: null,
    notes: '',
    rejectionReason: '',
    targetTier: 3
  });

  // Image modal state
  const [imageModal, setImageModal] = useState({
    open: false,
    imageUrl: '',
    title: '',
    fileName: ''
  });

  // Fetch KYC submissions
  const fetchKYCSubmissions = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page,
        limit: rowsPerPage,
      };

      // Only add filters if they have valid values
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (incomeFilter !== 'all') {
        params.annualIncome = incomeFilter;
      }
      if (sourceFilter !== 'all') {
        params.sourceOfFunds = sourceFilter;
      }
      if (search && search.trim()) {
        params.search = search.trim();
      }

      const response = await kycService.getAllKYCSubmissions(params);

      if (response && response.success) {
        setKycSubmissions(response.data || []);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalItems(response.pagination?.totalItems || 0);
      } else {
        setError('Invalid response from server');
        showToast('Invalid response from server', 'error');
      }
    } catch (error) {
      setError(error.message || 'Error fetching KYC submissions');
      showToast(error.message || 'Error fetching KYC submissions', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStatistics = async () => {
    try {
      console.log('Fetching KYC statistics...');
      const response = await kycService.getKYCStatistics();
      console.log('Statistics response:', response);
      console.log('Statistics data:', response.data);

      // Ensure the statistics have all required fields
      const stats = {
        pending: response.data?.pending || 0,
        verified: response.data?.verified || 0,
        rejected: response.data?.rejected || 0,
        expired: response.data?.expired || 0,
        total: response.data?.total || 0
      };

      // Calculate total if it's not provided
      if (!response.data?.total) {
        stats.total = stats.pending + stats.verified + stats.rejected + stats.expired;
      }

      console.log('Processed statistics:', stats);
      setStatistics(stats);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchKYCSubmissions();
    fetchStatistics();
  }, [page, rowsPerPage, statusFilter, incomeFilter, sourceFilter, search]);

  const handleViewDetails = async (submission) => {
    try {
      const response = await kycService.getKYCSubmissionById(submission._id);
      setSelectedSubmission(response.data);
      setOpenDialog(true);
    } catch (error) {
      showToast(error.message || 'Error fetching submission details', 'error');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedSubmission(null);
  };

  const handleVerify = (submission) => {
    setVerificationDialog({
      open: true,
      type: 'verify',
      targetTier: 3,
      submissionId: submission._id
    });
  };

  const handleReject = (submission) => {
    setVerificationDialog({
      open: true,
      type: 'reject',
      targetTier: 3,
      submissionId: submission._id
    });
  };

  const handleVerificationSubmit = async () => {
    try {
      setVerificationLoading(true);

      if (verificationDialog.type === 'verify') {
        await kycService.verifyKYCApplication(
          verificationDialog.submissionId,
          verificationDialog.notes,
          verificationDialog.targetTier
        );
        showToast('KYC application verified successfully!', 'success');
      } else {
        await kycService.rejectKYCApplication(
          verificationDialog.submissionId,
          verificationDialog.rejectionReason,
          verificationDialog.notes
        );
        showToast('KYC application rejected successfully!', 'success');
      }

      setVerificationDialog({ open: false, type: '', submissionId: null, notes: '', rejectionReason: '', targetTier: 3 });
      fetchKYCSubmissions(); // Refresh the list
      fetchStatistics(); // Refresh statistics
    } catch (error) {
      showToast(error.message || 'Error processing KYC application', 'error');
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleCloseVerificationDialog = () => {
    setVerificationDialog({ open: false, type: '', submissionId: null, notes: '', rejectionReason: '', targetTier: 3 });
  };

  // Image modal handlers
  const handleOpenImage = (imageUrl, title, fileName) => {
    setImageModal({
      open: true,
      imageUrl,
      title,
      fileName
    });
  };

  const handleCloseImage = () => {
    setImageModal({ open: false, imageUrl: '', title: '', fileName: '' });
  };

  const handleDownloadImage = async () => {
    try {
      const response = await fetch(imageModal.imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = imageModal.fileName || 'document.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showToast('Document downloaded successfully!', 'success');
    } catch (error) {
      showToast('Error downloading document', 'error');
    }
  };

  const handleRefresh = () => {
    fetchKYCSubmissions();
    fetchStatistics();
  };

  const handleClearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setIncomeFilter('all');
    setSourceFilter('all');
    setPage(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'warning';
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" fontWeight={700} color="primary">
          KYC Verification
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'warning.light', color: 'white' }}>
            <CardContent>
              <Typography variant="h4" fontWeight={700}>
                {statistics.pending}
              </Typography>
              <Typography variant="body2">Pending</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'success.light', color: 'white' }}>
            <CardContent>
              <Typography variant="h4" fontWeight={700}>
                {statistics.verified}
              </Typography>
              <Typography variant="body2">Verified</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'error.light', color: 'white' }}>
            <CardContent>
              <Typography variant="h4" fontWeight={700}>
                {statistics.rejected}
              </Typography>
              <Typography variant="body2">Rejected</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'info.light', color: 'white' }}>
            <CardContent>
              <Typography variant="h4" fontWeight={700}>
                {statistics.total || 0}
              </Typography>
              <Typography variant="body2">Total</Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                {/* Debug: {JSON.stringify(statistics)} */}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, email, phone, occupation, employer, or nationality..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="verified">Verified</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Income Range</InputLabel>
              <Select
                value={incomeFilter}
                onChange={(e) => setIncomeFilter(e.target.value)}
                label="Income Range"
              >
                <MenuItem value="all">All Income</MenuItem>
                <MenuItem value="below_500k">Below 500k</MenuItem>
                <MenuItem value="500k_1m">500k - 1M</MenuItem>
                <MenuItem value="1m_5m">1M - 5M</MenuItem>
                <MenuItem value="5m_10m">5M - 10M</MenuItem>
                <MenuItem value="above_10m">Above 10M</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Source of Funds</InputLabel>
              <Select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                label="Source of Funds"
              >
                <MenuItem value="all">All Sources</MenuItem>
                <MenuItem value="salary">Salary</MenuItem>
                <MenuItem value="business">Business</MenuItem>
                <MenuItem value="investment">Investment</MenuItem>
                <MenuItem value="inheritance">Inheritance</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Rows Per Page</InputLabel>
              <Select
                value={rowsPerPage}
                onChange={(e) => setRowsPerPage(e.target.value)}
                label="Rows Per Page"
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              fullWidth
              size="small"
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Full Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Occupation</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="error">{error}</Typography>
                  </TableCell>
                </TableRow>
              ) : kycSubmissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography>No KYC submissions found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                kycSubmissions.map((submission) => (
                  <TableRow key={submission._id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {submission.userId?.fullname || submission.userId?.username || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {submission.userId?.username}
                      </Typography>
                    </TableCell>
                    <TableCell>{submission.fullName}</TableCell>
                    <TableCell>{submission.email}</TableCell>
                    <TableCell>{submission.phone}</TableCell>
                    <TableCell>{submission.occupation || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={submission.status}
                        color={getStatusColor(submission.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(submission.submittedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleViewDetails(submission)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      {submission.status === 'pending' && (
                        <>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleVerify(submission)}
                          >
                            <CheckIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleReject(submission)}
                          >
                            <CloseIcon />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      {/* KYC Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          }
        }}
      >
        {selectedSubmission && (
          <>
            <DialogTitle sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: '12px 12px 0 0',
              pb: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    KYC Submission Details
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                    {selectedSubmission.fullName} • {selectedSubmission.status}
                  </Typography>
                </Box>
                <Chip
                  label={selectedSubmission.status}
                  color={getStatusColor(selectedSubmission.status)}
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    '& .MuiChip-label': { color: 'white' }
                  }}
                />
              </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {/* Left Column - User Information */}
                <Grid item xs={12} md={6}>
                  <Card sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'linear-gradient(145deg, #f8f9fa 0%, #e9ecef 100%)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                      👤 User Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Full Name
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ color: '#2c3e50' }}>
                          {selectedSubmission.fullName}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Email
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ color: '#2c3e50' }}>
                          {selectedSubmission.email}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Phone
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ color: '#2c3e50' }}>
                          {selectedSubmission.phone}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Date of Birth
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ color: '#2c3e50' }}>
                          {new Date(selectedSubmission.dateOfBirth).toLocaleDateString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Gender
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ color: '#2c3e50' }}>
                          {selectedSubmission.gender}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Nationality
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ color: '#2c3e50' }}>
                          {selectedSubmission.nationality}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Address
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ color: '#2c3e50' }}>
                          {selectedSubmission.address?.street}, {selectedSubmission.address?.city}, {selectedSubmission.address?.state} {selectedSubmission.address?.postalCode}, {selectedSubmission.address?.country}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Card>

                  <Card sx={{
                    p: 3,
                    mt: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(145deg, #f8f9fa 0%, #e9ecef 100%)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                      💼 Additional Information
                    </Typography>
                    <Grid container spacing={2}>
                      {selectedSubmission.occupation && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Occupation
                          </Typography>
                          <Typography variant="body1" fontWeight={600} sx={{ color: '#2c3e50' }}>
                            {selectedSubmission.occupation}
                          </Typography>
                        </Grid>
                      )}
                      {selectedSubmission.employer && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Employer
                          </Typography>
                          <Typography variant="body1" fontWeight={600} sx={{ color: '#2c3e50' }}>
                            {selectedSubmission.employer}
                          </Typography>
                        </Grid>
                      )}
                      {selectedSubmission.annualIncome && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Annual Income
                          </Typography>
                          <Typography variant="body1" fontWeight={600} sx={{ color: '#2c3e50' }}>
                            {selectedSubmission.annualIncome.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </Typography>
                        </Grid>
                      )}
                      {selectedSubmission.sourceOfFunds && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Source of Funds
                          </Typography>
                          <Typography variant="body1" fontWeight={600} sx={{ color: '#2c3e50' }}>
                            {selectedSubmission.sourceOfFunds.charAt(0).toUpperCase() + selectedSubmission.sourceOfFunds.slice(1)}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Card>
                </Grid>

                {/* Right Column - Documents and Status */}
                <Grid item xs={12} md={6}>
                  <Card sx={{
                    p: 3,
                    borderRadius: 2,
                    background: 'linear-gradient(145deg, #f8f9fa 0%, #e9ecef 100%)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                      📄 Documents
                    </Typography>
                    <Grid container spacing={2}>
                      {selectedSubmission.documents?.idCard?.front && (
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Box
                              sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 8px',
                                cursor: 'pointer',
                                transition: 'transform 0.2s',
                                '&:hover': {
                                  transform: 'scale(1.05)',
                                }
                              }}
                              onClick={() => handleOpenImage(
                                selectedSubmission.documents.idCard.front,
                                'ID Card Front',
                                `${selectedSubmission.fullName}_ID_Front.jpg`
                              )}
                            >
                              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                                ID
                              </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                              ID Card Front
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {selectedSubmission.documents?.idCard?.back && (
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Box
                              sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 8px',
                                cursor: 'pointer',
                                transition: 'transform 0.2s',
                                '&:hover': {
                                  transform: 'scale(1.05)',
                                }
                              }}
                              onClick={() => handleOpenImage(
                                selectedSubmission.documents.idCard.back,
                                'ID Card Back',
                                `${selectedSubmission.fullName}_ID_Back.jpg`
                              )}
                            >
                              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                                ID
                              </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                              ID Card Back
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {selectedSubmission.documents?.selfie && (
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Box
                              sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 8px',
                                cursor: 'pointer',
                                transition: 'transform 0.2s',
                                '&:hover': {
                                  transform: 'scale(1.05)',
                                }
                              }}
                              onClick={() => handleOpenImage(
                                selectedSubmission.documents.selfie,
                                'Selfie',
                                `${selectedSubmission.fullName}_Selfie.jpg`
                              )}
                            >
                              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                                👤
                              </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                              Selfie
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {selectedSubmission.documents?.proofOfAddress && (
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Box
                              sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 8px',
                                cursor: 'pointer',
                                transition: 'transform 0.2s',
                                '&:hover': {
                                  transform: 'scale(1.05)',
                                }
                              }}
                              onClick={() => handleOpenImage(
                                selectedSubmission.documents.proofOfAddress,
                                'Proof of Address',
                                `${selectedSubmission.fullName}_ProofOfAddress.jpg`
                              )}
                            >
                              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                                🏠
                              </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                              Proof of Address
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {selectedSubmission.documents?.utilityBill && (
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Box
                              sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 8px',
                                cursor: 'pointer',
                                transition: 'transform 0.2s',
                                '&:hover': {
                                  transform: 'scale(1.05)',
                                }
                              }}
                              onClick={() => handleOpenImage(
                                selectedSubmission.documents.utilityBill,
                                'Utility Bill',
                                `${selectedSubmission.fullName}_UtilityBill.jpg`
                              )}
                            >
                              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                                📋
                              </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                              Utility Bill
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Card>

                  <Card sx={{
                    p: 3,
                    mt: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(145deg, #f8f9fa 0%, #e9ecef 100%)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                      📊 Status Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Status
                        </Typography>
                        <Chip
                          label={selectedSubmission.status}
                          color={getStatusColor(selectedSubmission.status)}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Submitted At
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ color: '#2c3e50' }}>
                          {new Date(selectedSubmission.submittedAt).toLocaleString()}
                        </Typography>
                      </Grid>
                      {selectedSubmission.verificationDetails?.verifiedBy && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Verified By
                          </Typography>
                          <Typography variant="body1" fontWeight={600} sx={{ color: '#2c3e50' }}>
                            {selectedSubmission.verificationDetails.verifiedBy?.fullname || 'Admin'}
                          </Typography>
                        </Grid>
                      )}
                      {selectedSubmission.verificationDetails?.verifiedAt && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Verified At
                          </Typography>
                          <Typography variant="body1" fontWeight={600} sx={{ color: '#2c3e50' }}>
                            {new Date(selectedSubmission.verificationDetails.verifiedAt).toLocaleString()}
                          </Typography>
                        </Grid>
                      )}
                      {selectedSubmission.expiresAt && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Expires At
                          </Typography>
                          <Typography variant="body1" fontWeight={600} sx={{ color: '#2c3e50' }}>
                            {new Date(selectedSubmission.expiresAt).toLocaleDateString()}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Card>
                </Grid>
              </Grid>

              {/* Additional Information Cards */}
              <Grid container spacing={3} sx={{ mt: 2 }}>
                {selectedSubmission.verificationDetails?.rejectionReason && (
                  <Grid item xs={12}>
                    <Card sx={{
                      p: 3,
                      borderRadius: 2,
                      background: 'linear-gradient(145deg, #ffebee 0%, #ffcdd2 100%)',
                      border: '1px solid #ef5350'
                    }}>
                      <Typography variant="h6" sx={{ mb: 2, color: 'error.main', fontWeight: 600 }}>
                        ❌ Rejection Details
                      </Typography>
                      <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                        <Typography variant="body2" color="error.dark" fontWeight={600}>
                          Reason: {selectedSubmission.verificationDetails.rejectionReason}
                        </Typography>
                        {selectedSubmission.verificationDetails.rejectionNotes && (
                          <Typography variant="body2" color="error.dark" sx={{ mt: 1 }}>
                            Notes: {selectedSubmission.verificationDetails.rejectionNotes}
                          </Typography>
                        )}
                      </Box>
                    </Card>
                  </Grid>
                )}

                {selectedSubmission.verificationDetails?.adminNotes && (
                  <Grid item xs={12}>
                    <Card sx={{
                      p: 3,
                      borderRadius: 2,
                      background: 'linear-gradient(145deg, #e3f2fd 0%, #bbdefb 100%)',
                      border: '1px solid #42a5f5'
                    }}>
                      <Typography variant="h6" sx={{ mb: 2, color: 'info.main', fontWeight: 600 }}>
                        📝 Admin Notes
                      </Typography>
                      <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                        <Typography variant="body2" color="info.dark">
                          {selectedSubmission.verificationDetails.adminNotes}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Close</Button>
              {selectedSubmission.status === 'pending' && (
                <>
                  <Button
                    onClick={() => handleVerify(selectedSubmission)}
                    color="success"
                    variant="contained"
                  >
                    Verify
                  </Button>
                  <Button
                    onClick={() => handleReject(selectedSubmission)}
                    color="error"
                    variant="contained"
                  >
                    Reject
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Verification Dialog */}
      <Dialog
        open={verificationDialog.open}
        onClose={handleCloseVerificationDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {verificationDialog.type === 'verify' ? 'Verify KYC Application' : 'Reject KYC Application'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {verificationDialog.type === 'reject' && (
              <TextField
                fullWidth
                label="Rejection Reason"
                value={verificationDialog.rejectionReason}
                onChange={(e) => setVerificationDialog(prev => ({ ...prev, rejectionReason: e.target.value }))}
                required
                sx={{ mb: 2 }}
              />
            )}

            {verificationDialog.type === 'verify' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Target KYC Tier</InputLabel>
                <Select
                  value={verificationDialog.targetTier}
                  label="Target KYC Tier"
                  onChange={(e) => setVerificationDialog(prev => ({ ...prev, targetTier: e.target.value }))}
                >
                  <MenuItem value={1}>Level 1 (NIN Verified)</MenuItem>
                  <MenuItem value={2}>Level 2 (BVN Verified)</MenuItem>
                  <MenuItem value={3}>Level 3 (Full Documents)</MenuItem>
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Select the appropriate tier. Level 3 is standard for full document approval.
                </Typography>
              </FormControl>
            )}
            <TextField
              fullWidth
              label="Notes (Optional)"
              value={verificationDialog.notes}
              onChange={(e) => setVerificationDialog(prev => ({ ...prev, notes: e.target.value }))}
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVerificationDialog}>Cancel</Button>
          <Button
            onClick={handleVerificationSubmit}
            variant="contained"
            color={verificationDialog.type === 'verify' ? 'success' : 'error'}
            disabled={verificationLoading || (verificationDialog.type === 'reject' && !verificationDialog.rejectionReason)}
          >
            {verificationLoading ? (
              <CircularProgress size={20} />
            ) : (
              verificationDialog.type === 'verify' ? 'Verify' : 'Reject'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Modal */}
      <Dialog
        open={imageModal.open}
        onClose={handleCloseImage}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: '12px 12px 0 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" fontWeight={600}>
            {imageModal.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={handleDownloadImage}
              sx={{ color: 'white' }}
              title="Download"
            >
              <DownloadIcon />
            </IconButton>
            <IconButton
              onClick={handleCloseImage}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Box
            sx={{
              width: '100%',
              height: '70vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              background: '#f5f5f5',
              position: 'relative'
            }}
          >
            <img
              src={imageModal.imageUrl}
              alt={imageModal.title}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: 8
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <Box
              sx={{
                display: 'none',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'text.secondary'
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                Image not available
              </Typography>
              <Typography variant="body2">
                The document image could not be loaded
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default KYCVerification; 